import React, { useState, useContext, useEffect } from "react";
import { submitCreateAssistant, assistantsList, getUserId, enableAssistant } from "../../../api/api";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../../authConfig";
import { AppStateContext } from "../../../state/AppProvider";
import { UserSearchBox } from "../ui/UserSearchBox";
import { SearchEntity } from "../../../api/models";
import ClipLoader from "react-spinners/ClipLoader";
import { ErrorDialog } from "../../ErrorDialog";
import { TitleField } from "../ui/TitleField";
import { PromptField } from "../ui/PromptField";
import { ExamplesField } from "../ui/ExamplesField";
import { ContactsField } from "../ui/ContactsField";
import { FileUploadField } from "../ui/FileUploadField";
import { useKnowledgeUpload } from "../../../hooks/useKnowledgeUpload";
import { useFileProcessing } from "../../../hooks/useFileProcessing";
import { FileProcessingModal } from "../ui/FileProcessingModal";
import "../ui/AssistantBuilder.css";

interface AssistantCreatorProps {
    onSubmit: () => void;
    onCancel: () => void;
}

export const AssistantCreator = ({ onSubmit, onCancel }: AssistantCreatorProps) => {
    const { instance: client } = useMsal();
    const appStateContext = useContext(AppStateContext);

    const knowledge_limit = 500;
    const knowledge_size = 500; // mb

    const [title, setTitle] = useState("");
    const [promptMessage, setPromptMessage] = useState("");
    const [examples, setExamples] = useState<string[]>(["", "", ""]);
    const [contacts, setContacts] = useState<{ name: string; email: string }>({ name: "", email: "" });

    const [userEntities, setUserEntities] = useState<SearchEntity[]>([]);
    const [adminEntities, setAdminEntities] = useState<SearchEntity[]>([]);
    const [fileManagerEntities, setFileManagerEntities] = useState<SearchEntity[]>([]);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [currentPhase, setCurrentPhase] = useState<string>("");

    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const openErrorDialog = (msg: string) => {
        setErrorMessage(msg);
        setShowErrorDialog(true);
    };

    const { files: uploadedFiles, addFiles, removeFile } = useKnowledgeUpload(knowledge_limit, knowledge_size, openErrorDialog);

    const { currentFileProgress, currentFileIndex, totalFiles, uploadDocument, resetProgress } = useFileProcessing();

    useEffect(() => {
        if (isSuccess) {
            const timer = setTimeout(() => {
                onSubmit();
                onCancel();
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [isSuccess, onSubmit, onCancel]);

    const refreshAssistantsList = async () => {
        const token = client ? await getToken(client) : undefined;
        const userId = (await getUserId(token?.accessToken)).userId;
        const list = await assistantsList(token?.accessToken).catch(() => null);
        if (!list) {
            appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: null });
            appStateContext?.dispatch({ type: "UPDATE_CUSTOM_ASSISTANTS", payload: null });
            appStateContext?.dispatch({ type: "UPDATE_SHARED_ASSISTANTS", payload: null });
            return null;
        }
        appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: list });
        appStateContext?.dispatch({
            type: "UPDATE_CUSTOM_ASSISTANTS",
            payload: list.filter(a => a.created_by === userId && a.type === "custom")
        });
        appStateContext?.dispatch({
            type: "UPDATE_SHARED_ASSISTANTS",
            payload: list.filter(a => a.created_by !== userId && a.type === "custom")
        });
        return list;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (isDisabled) return;
        setIsDisabled(true);
        setFormSubmitted(true);

        if (!title || !promptMessage) {
            setIsDisabled(false);
            return;
        }

        if (!contacts.name || !contacts.email) {
            openErrorDialog("Contact name and email are required.");
            setIsDisabled(false);
            return;
        }

        try {
            const token = client ? await getToken(client) : undefined;
            setIsSubmitting(true);
            setCurrentPhase("Initialising assistant...");
            resetProgress();

            const userIds = userEntities.filter(e => e.objectType === "user").map(e => e.id);
            const groupIds = userEntities.filter(e => e.objectType === "group").map(e => e.id);
            const adminIds = adminEntities.filter(e => e.objectType === "user").map(e => e.id);
            const adminGroupIds = adminEntities.filter(e => e.objectType === "group").map(e => e.id);
            const fileManagerIds = fileManagerEntities.filter(e => e.objectType === "user").map(e => e.id);
            const fileManagerGroupIds = fileManagerEntities.filter(e => e.objectType === "group").map(e => e.id);

            const { ok, data } = await submitCreateAssistant(
                title,
                promptMessage,
                userIds,
                groupIds,
                adminIds,
                adminGroupIds,
                fileManagerIds,
                fileManagerGroupIds,
                examples.filter(Boolean),
                contacts,
                token?.accessToken
            );

            if (!ok) {
                openErrorDialog(data?.error ?? "Failed to create your assistant.");
                setIsDisabled(false);
                setIsSubmitting(false);
                return;
            }

            const assistantId = data?.id;

            // Process files one at a time if any
            if (uploadedFiles.length > 0) {
                const allErrors: Array<{ filename: string; error: string }> = [];

                for (let i = 0; i < uploadedFiles.length; i++) {
                    const file = uploadedFiles[i];
                    setCurrentPhase(`Processing document ${i + 1} of ${uploadedFiles.length}: ${file.name}`);

                    const result = await uploadDocument(assistantId, file, i, uploadedFiles.length, token?.accessToken);

                    if (!result.success) {
                        allErrors.push({
                            filename: file.name,
                            error: result.error || "Unknown error"
                        });
                    }
                }

                if (allErrors.length > 0) {
                    const msg = allErrors.map(e => `${e.filename}: ${e.error}`).join("\n");
                    openErrorDialog(`Some files failed:\n${msg}`);
                    setIsDisabled(false);
                    setIsSubmitting(false);
                    return;
                }
            } else {
                // If no files, still need to enable the assistant
                await enableAssistant(assistantId, token?.accessToken);
            }

            // Refresh list and show success
            setCurrentPhase("Finalising assistant...");
            await refreshAssistantsList();
            setIsSuccess(true);
            setCurrentPhase("");
        } catch (error) {
            openErrorDialog("An error occurred while submitting the form");
            setIsDisabled(false);
            setIsSubmitting(false);
            setCurrentPhase("");
        }
    };

    return (
        <div className="relative flex flex-col h-full">
            {/* File Processing Modal */}
            <FileProcessingModal
                isVisible={isSubmitting && !isSuccess && uploadedFiles.length > 0}
                currentPhase={currentPhase}
                currentFileProgress={currentFileProgress}
                currentFileIndex={currentFileIndex}
                totalFiles={totalFiles}
                isEditing={false}
            />

            {/* Original loading spinner (for when no files) */}
            {isSubmitting && !isSuccess && uploadedFiles.length === 0 && (
                <>
                    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm" />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div className="bg-eq-light-grey p-6 rounded-sm shadow-lg pointer-events-auto flex flex-col items-center">
                            <ClipLoader size={40} color="#6D3E91" />
                            <h2 className="text-eq-grey text-md font-semibold mt-4 mb-2 text-center">Your assistant is being created...</h2>
                            <p className="text-eq-grey text-sm text-center">Please wait while we set everything up.</p>
                        </div>
                    </div>
                </>
            )}

            {/* Success overlay */}
            {isSuccess && (
                <>
                    <div className="fixed inset-0 z-50 bg-white" />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div className="flex flex-col justify-center items-center pointer-events-auto">
                            <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
                                <path className="checkmark__check" fill="none" d="M14 27l7 7 16-16" />
                            </svg>
                            <h2 className="text-xl font-semibold text-eq-green mt-4">Assistant Created!</h2>
                        </div>
                    </div>
                </>
            )}

            <div className="flex flex-col h-full">
                {/* Header Section */}
                <div className="sticky top-0 z-10 bg-white px-4 pt-6 pb-6 sm:px-6 border-b border-gray-200">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100">
                            <span className="material-symbols-outlined text-[1.5rem] text-eq-green">smart_toy</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold leading-6 text-gray-900">Create Custom Assistant</h3>
                            <p className="mt-1 text-sm text-gray-500">Build a specialised assistant with custom knowledge and behavior to help your team.</p>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className={`flex-grow px-8 py-6 transition-opacity ${isSubmitting ? "opacity-50 pointer-events-none" : ""}`}>
                    <div className="mb-6">
                        <TitleField value={title} onChange={setTitle} formSubmitted={formSubmitted} disabled={isSubmitting} />
                    </div>

                    <div className="mb-6">
                        <PromptField value={promptMessage} onChange={setPromptMessage} formSubmitted={formSubmitted} disabled={isSubmitting} />
                    </div>

                    <div className="mb-6">
                        <h4 className={`text-lg font-semibold ${isSubmitting ? "text-gray-400" : "text-eq-grey"}`}>Add Users</h4>
                        <p className={`text-sm mt-1 mb-4 ${isSubmitting ? "text-gray-400" : "text-gray-500"}`}>Share your assistant with other users.</p>
                        <UserSearchBox
                            onSelectEntity={e => setUserEntities(p => [...p, e])}
                            selectedEntities={userEntities}
                            onRemoveEntity={id => {
                                setUserEntities(p => p.filter(e => e.id !== id));
                                setAdminEntities(p => p.filter(e => e.id !== id));
                                setFileManagerEntities(p => p.filter(e => e.id !== id));
                            }}
                        />
                    </div>

                    <div className="mb-6">
                        <h4 className={`text-lg font-semibold ${isSubmitting ? "text-gray-400" : "text-eq-grey"}`}>Add Admins</h4>
                        <p className={`text-sm mt-1 mb-4 ${isSubmitting ? "text-gray-400" : "text-gray-500"}`}>Add admin users for this assistant.</p>
                        <UserSearchBox
                            onSelectEntity={e => {
                                setAdminEntities(p => [...p, e]);
                                setUserEntities(p => (p.some(u => u.id === e.id) ? p : [...p, e]));
                            }}
                            selectedEntities={adminEntities}
                            onRemoveEntity={id => setAdminEntities(p => p.filter(e => e.id !== id))}
                        />
                    </div>

                    <div className="mb-6">
                        <h4 className={`text-lg font-semibold ${isSubmitting ? "text-gray-400" : "text-eq-grey"}`}>Add File Managers</h4>
                        <p className={`text-sm mt-1 mb-4 ${isSubmitting ? "text-gray-400" : "text-gray-500"}`}>Add file manager users for this assistant.</p>
                        <UserSearchBox
                            onSelectEntity={e => {
                                setFileManagerEntities(p => [...p, e]);
                                setUserEntities(p => (p.some(u => u.id === e.id) ? p : [...p, e]));
                            }}
                            selectedEntities={fileManagerEntities}
                            onRemoveEntity={id => setFileManagerEntities(p => p.filter(e => e.id !== id))}
                        />
                    </div>

                    <div className="mb-6">
                        <ExamplesField examples={examples} onChange={setExamples} disabled={isSubmitting} />
                    </div>

                    <div className="mb-6">
                        <ContactsField
                            contacts={[contacts]}
                            onChange={vals => setContacts(vals[0] ?? { name: "", email: "" })}
                            disabled={isSubmitting}
                            formSubmitted={formSubmitted}
                        />
                    </div>

                    <div className="mb-6">
                        <FileUploadField
                            files={uploadedFiles}
                            addFiles={addFiles}
                            removeFile={removeFile}
                            knowledgeCountLimit={knowledge_limit}
                            knowledgeSizeLimit={knowledge_size}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div
                    className={`sticky bottom-0 border-t border-gray-200 bg-white flex flex-row gap-4 px-4 pb-6 pt-4 sm:flex-row-reverse sm:px-6 ${isSubmitting || isSuccess ? "z-[60]" : "z-10"}`}
                >
                    {!isSuccess && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isDisabled || isSubmitting}
                            className={`eq-primary-button flex items-center justify-center ${
                                isSubmitting ? "opacity-100" : "bg-eq-green hover:bg-eq-green"
                            } disabled:cursor-not-allowed`}
                        >
                            {isSubmitting ? <ClipLoader size={16} color="white" /> : "Create"}
                        </button>
                    )}
                    <button type="button" className="eq-secondary-button hover:bg-eq-light-grey" onClick={onCancel}>
                        Close
                    </button>
                </div>
            </div>

            <ErrorDialog open={showErrorDialog} setOpen={setShowErrorDialog} errorMessage={errorMessage} />
        </div>
    );
};
